import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { BusyButton } from "./shared";
import {
  getBillingCatalogPlansAdmin,
  getPlatformDominioSubscriptions,
  getPlatformFinanceCommissionsSales,
  getPlatformFinanceCommissionsSalesPreview,
  getPlatformFinanceCommissionsTeamDefaults,
  listPlatformDominios,
  postPlatformFinanceCommissionsSales,
  postPlatformFinanceCommissionsSalesCancel,
  putPlatformFinanceCommissionsTeamDefaults,
  putPlatformFinancePeople,
} from "../../../api/client";
import {
  FinanceAlertStack,
  FinanceEmptyState,
  FinanceLoading,
  FinanceSection,
  FinanceTableShell,
  formatBrlFromCents,
  formatFinanceDateBr,
} from "./FinanceUi";
import { financeAiCfg } from "./FinanceMetricAi";
import { FINANCE_GLOSS, FINANCE_LABEL } from "./FinanceCopy";
import {
  FIELD_LIMITS,
  clampInt,
  inputToPct,
  isDateInYearMonth,
  moneyReaisToCents,
  parseMoneyReais,
  pctToInput,
  sanitizeDecimalTyping,
  sanitizeIntTyping,
  sanitizePctTyping,
  yearMonthBounds,
} from "./financeFormFields";

const LINE_ROLE_LABEL = {
  closer: "Closer",
  indicator: "Indicador",
  leader_on_closer: "Gestor (sobre closer)",
  leader_on_indicator: "Gestor (sobre indicador)",
};

const SALE_KIND_CHIP = {
  new: "Novo cliente",
  addon: "Nova assinatura",
  upgrade: "Upgrade",
};

function catalogPlanMarginCents(p) {
  const mrr = Number(p?.computed_mrr_cents || 0);
  const cmv = Number(p?.computed_cmv_cents || 0);
  const tax = Number(p?.computed_tax_cents || 0);
  let m = Number(p?.computed_margin_cents || 0);
  if (!m && mrr > 0) m = Math.max(0, mrr - cmv - tax);
  return Math.round(m);
}

function centsToReaisInput(cents) {
  const n = Number(cents || 0) / 100;
  if (!Number.isFinite(n)) return "";
  return String(n);
}

function saleKindHint(kind) {
  if (kind === "upgrade") return FINANCE_GLOSS.saleKindUpgrade;
  if (kind === "addon") return FINANCE_GLOSS.saleKindAddon;
  return FINANCE_GLOSS.saleKindNew;
}

function formatPct(v) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  return `${Math.round(Number(v) * 10000) / 100}%`;
}

function moneyFieldError(raw, { required = false, label = "Valor" } = {}) {
  if (raw === "" || raw == null) {
    return required ? `${label} é obrigatório.` : null;
  }
  if (String(raw).endsWith(".")) return `${label}: complete as casas decimais.`;
  const n = parseMoneyReais(raw);
  if (n == null) return `${label} inválido.`;
  if (n > FIELD_LIMITS.moneyMaxReais) return `${label} acima do limite permitido.`;
  return null;
}

export function SalesTab({ yearMonth }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [people, setPeople] = useState([]);
  const [wsOptions, setWsOptions] = useState([]);
  const [wsLoading, setWsLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [catalogPlans, setCatalogPlans] = useState([]);
  const [workspaceSubs, setWorkspaceSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [draft, setDraft] = useState({
    sale_kind: "new",
    dominio: null,
    closer_person_id: "",
    indicator_person_id: "",
    previous_catalog_plan_id: "",
    subscription_id: "",
    previous_mrr_reais: "",
    new_mrr_reais: "",
    notes: "",
    sold_on: `${yearMonth}-01`,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPlatformFinanceCommissionsSales({ month: yearMonth });
      setItems((res?.items || []).filter((s) => s.status !== "cancelled"));
      setPeople(res?.people || []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Não foi possível carregar vendas.");
    } finally {
      setLoading(false);
    }
  }, [yearMonth]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getBillingCatalogPlansAdmin();
        if (!cancelled) setCatalogPlans(res?.items || []);
      } catch {
        if (!cancelled) setCatalogPlans([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setDraft((d) => ({
      ...d,
      sold_on: d.sold_on?.startsWith(yearMonth) ? d.sold_on : `${yearMonth}-01`,
    }));
  }, [yearMonth]);

  const loadWorkspaceSubs = useCallback(async (dominioId) => {
    if (!dominioId) {
      setWorkspaceSubs([]);
      return;
    }
    setSubsLoading(true);
    try {
      const res = await getPlatformDominioSubscriptions(dominioId);
      setWorkspaceSubs(res?.subscriptions || []);
    } catch {
      setWorkspaceSubs([]);
    } finally {
      setSubsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkspaceSubs(draft.dominio?.id);
  }, [draft.dominio?.id, loadWorkspaceSubs]);

  const activeSubs = useMemo(
    () =>
      (workspaceSubs || []).filter((s) =>
        ["active", "trialing", "past_due", "onboarding", "courtesy"].includes(String(s.status || ""))
      ),
    [workspaceSubs]
  );

  const cancelledSubsWithPlan = useMemo(
    () =>
      (workspaceSubs || []).filter(
        (s) => String(s.status || "") === "cancelled" && Number(s.catalog_plan_id) > 0
      ),
    [workspaceSubs]
  );

  const previousPlanOptions = useMemo(() => {
    const byId = new Map((catalogPlans || []).map((p) => [Number(p.id), p]));
    const fromCancelled = [];
    const seen = new Set();
    for (const s of cancelledSubsWithPlan) {
      const id = Number(s.catalog_plan_id);
      if (!(id > 0) || seen.has(id)) continue;
      seen.add(id);
      const plan = byId.get(id) || {};
      fromCancelled.push({
        id,
        name: plan.name || s.catalog_plan_name || s.plan_name || `Plano #${id}`,
        sku: plan.sku || s.catalog_plan_sku || "",
        margin_cents: catalogPlanMarginCents(plan),
        source: "cancelled_sub",
      });
    }
    const fromCatalog = (catalogPlans || [])
      .map((p) => ({
        id: Number(p.id),
        name: p.name,
        sku: p.sku || "",
        margin_cents: catalogPlanMarginCents(p),
        source: "catalog",
      }))
      .filter((p) => p.id > 0 && !seen.has(p.id));
    return [...fromCancelled, ...fromCatalog];
  }, [cancelledSubsWithPlan, catalogPlans]);

  const activeSubOptions = useMemo(() => {
    const byId = new Map((catalogPlans || []).map((p) => [Number(p.id), p]));
    return activeSubs.map((s) => {
      const plan = byId.get(Number(s.catalog_plan_id)) || {};
      return {
        id: Number(s.id),
        name: plan.name || s.catalog_plan_name || s.plan_name || `Assinatura #${s.id}`,
        sku: plan.sku || s.catalog_plan_sku || "",
        margin_cents: catalogPlanMarginCents(plan),
      };
    });
  }, [activeSubs, catalogPlans]);
  const searchWorkspaces = async (q) => {
    setWsLoading(true);
    try {
      const res = await listPlatformDominios({
        q: q || undefined,
        limit: 30,
        offset: 0,
        tenantLegacyStats: "0",
      });
      const items = res?.items || res?.rows || [];
      setWsOptions(
        items
          .map((d) => ({
            id: Number(d.id ?? d.dominio_id),
            label: d.dominio || d.nome || d.bd || `#${d.id}`,
            schema: d.bd || "",
          }))
          .filter((d) => Number.isFinite(d.id) && d.id > 0)
      );
    } catch {
      setWsOptions([]);
    } finally {
      setWsLoading(false);
    }
  };

  const activePeople = useMemo(
    () => (people || []).filter((p) => (p.status || "active") === "active"),
    [people]
  );

  const dateBounds = useMemo(() => yearMonthBounds(yearMonth), [yearMonth]);

  const fieldErrors = useMemo(() => {
    const e = {};
    if (!draft.dominio?.id) e.dominio = "Selecione o workspace.";
    if (!draft.closer_person_id) e.closer = "Selecione quem fechou a venda.";
    if (
      draft.indicator_person_id &&
      String(draft.indicator_person_id) === String(draft.closer_person_id)
    ) {
      e.indicator = "Indicador deve ser outra pessoa.";
    }
    if (!isDateInYearMonth(draft.sold_on, yearMonth)) {
      e.sold_on = `Data deve estar em ${formatFinanceDateBr(yearMonth)} (${formatFinanceDateBr(dateBounds.min)} a ${formatFinanceDateBr(dateBounds.max)}).`;
    }
    const newErr = moneyFieldError(draft.new_mrr_reais, { label: "Margem" });
    if (newErr) e.new_mrr = newErr;
    if (draft.sale_kind === "upgrade") {
      if (!draft.previous_catalog_plan_id && !String(draft.previous_mrr_reais || "").trim()) {
        e.previous_plan = "Selecione o plano anterior (após migrar no workspace).";
      }
      if (activeSubOptions.length > 1 && !draft.subscription_id) {
        e.subscription = "Selecione qual assinatura sofreu o upgrade.";
      }
      const prevErr = moneyFieldError(draft.previous_mrr_reais, { label: "Margem anterior" });
      if (prevErr) e.previous_mrr = prevErr;
      const prev = parseMoneyReais(draft.previous_mrr_reais);
      const next = parseMoneyReais(draft.new_mrr_reais);
      if (prev != null && next != null && next <= prev) {
        e.new_mrr = "No upgrade, a margem nova deve ser maior que a anterior.";
      }
    }
    if (draft.sale_kind === "addon" && !draft.subscription_id) {
      e.subscription = "Selecione a nova assinatura deste cliente.";
    }
    if (String(draft.notes || "").length > FIELD_LIMITS.notesMax) {
      e.notes = `Máximo ${FIELD_LIMITS.notesMax} caracteres.`;
    }
    return e;
  }, [draft, yearMonth, dateBounds, activeSubOptions.length]);

  const canSave =
    Boolean(draft.dominio?.id) &&
    Boolean(draft.closer_person_id) &&
    !fieldErrors.closer &&
    !fieldErrors.indicator &&
    !fieldErrors.sold_on &&
    !fieldErrors.new_mrr &&
    !fieldErrors.previous_mrr &&
    !fieldErrors.previous_plan &&
    !fieldErrors.subscription &&
    !fieldErrors.notes &&
    !preview?.preview?.margin_missing;

  const refreshPreview = useCallback(async () => {
    if (!draft.dominio?.id) {
      setPreview(null);
      return;
    }
    try {
      const params = {
        dominio_id: draft.dominio.id,
        sale_kind: draft.sale_kind,
        month: yearMonth,
        closer_person_id: draft.closer_person_id || undefined,
        indicator_person_id: draft.indicator_person_id || undefined,
      };
      const newCents = moneyReaisToCents(draft.new_mrr_reais);
      if (newCents != null) params.new_mrr_cents = newCents;
      if (draft.sale_kind === "upgrade") {
        if (draft.previous_catalog_plan_id) {
          params.previous_catalog_plan_id = draft.previous_catalog_plan_id;
        }
        const prevCents = moneyReaisToCents(draft.previous_mrr_reais);
        if (prevCents != null) params.previous_mrr_cents = prevCents;
        if (draft.subscription_id) params.subscription_id = draft.subscription_id;
      }
      if (draft.sale_kind === "addon" && draft.subscription_id) {
        params.subscription_id = draft.subscription_id;
      }
      const res = await getPlatformFinanceCommissionsSalesPreview(params);
      setPreview(res);
    } catch (e) {
      setPreview({
        error: e?.response?.data?.message || e?.message || "Falha no preview.",
      });
    }
  }, [draft, yearMonth]);

  useEffect(() => {
    const t = setTimeout(() => {
      refreshPreview();
    }, 350);
    return () => clearTimeout(t);
  }, [refreshPreview]);

  useEffect(() => {
    if (
      (draft.sale_kind === "upgrade" || draft.sale_kind === "addon") &&
      activeSubOptions.length === 1 &&
      !draft.subscription_id
    ) {
      setDraft((d) => ({ ...d, subscription_id: String(activeSubOptions[0].id) }));
    }
  }, [draft.sale_kind, draft.subscription_id, activeSubOptions]);

  const save = async () => {
    if (!canSave) {
      setMsg({
        ok: false,
        text: Object.values(fieldErrors)[0] || "Revise os campos destacados.",
      });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      const payload = {
        dominio_id: draft.dominio.id,
        sale_kind: draft.sale_kind,
        closer_person_id: Number(draft.closer_person_id),
        indicator_person_id: draft.indicator_person_id
          ? Number(draft.indicator_person_id)
          : null,
        sold_on: draft.sold_on,
        period_ym: yearMonth,
        notes: (draft.notes || "").trim().slice(0, FIELD_LIMITS.notesMax) || null,
      };
      const newCents = moneyReaisToCents(draft.new_mrr_reais);
      if (newCents != null) payload.new_mrr_cents = newCents;
      if (draft.sale_kind === "upgrade") {
        if (draft.previous_catalog_plan_id) {
          payload.previous_catalog_plan_id = Number(draft.previous_catalog_plan_id);
        }
        const prevCents = moneyReaisToCents(draft.previous_mrr_reais);
        if (prevCents != null) payload.previous_mrr_cents = prevCents;
        if (draft.subscription_id) payload.subscription_id = Number(draft.subscription_id);
      }
      if (draft.sale_kind === "addon" && draft.subscription_id) {
        payload.subscription_id = Number(draft.subscription_id);
      }
      await postPlatformFinanceCommissionsSales(payload);
      setMsg({ ok: true, text: "Venda lançada." });
      setDraft((d) => ({
        ...d,
        indicator_person_id: "",
        notes: "",
        previous_mrr_reais: "",
        new_mrr_reais: "",
        previous_catalog_plan_id: "",
        subscription_id: "",
      }));
      await load();
    } catch (e) {
      setMsg({
        ok: false,
        text: e?.response?.data?.message || e?.message || "Falha ao lançar venda.",
      });
    } finally {
      setSaving(false);
    }
  };

  const cancelSale = async (id) => {
    setSaving(true);
    setMsg(null);
    try {
      await postPlatformFinanceCommissionsSalesCancel(id);
      setMsg({ ok: true, text: "Venda cancelada." });
      await load();
    } catch (e) {
      setMsg({
        ok: false,
        text: e?.response?.data?.message || e?.message || "Falha ao cancelar.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <FinanceLoading />;
  if (error) return <FinanceAlertStack error={error} />;

  const payoutLines = preview?.payout?.lines || [];
  const baseCents = preview?.preview?.base_cents;

  return (
    <Stack spacing={2}>
      {msg ? <Alert severity={msg.ok ? "success" : "error"}>{msg.text}</Alert> : null}
      <Alert severity="info" variant="outlined">
        {FINANCE_GLOSS.teamSalesHint}
      </Alert>
      <Alert severity="warning" variant="outlined">
        {FINANCE_GLOSS.teamVestingRules}
      </Alert>

      <FinanceSection
        title="Lançar venda"
        hint="Closer é obrigatório. Indicador é opcional (venda sem indicação)."
        ai={financeAiCfg({
          metric: "finance_comm_sale_form",
          name: FINANCE_LABEL.teamSales,
          value: { yearMonth },
          human: `Competência ${formatFinanceDateBr(yearMonth)}`,
          definition: FINANCE_GLOSS.teamSalesIntro,
        })}
      >
        <Stack spacing={1.5}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} useFlexGap flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <InputLabel>Tipo de lançamento</InputLabel>
              <Select
                label="Tipo de lançamento"
                value={draft.sale_kind}
                onChange={(e) => {
                  const sale_kind = e.target.value;
                  setDraft((d) => ({
                    ...d,
                    sale_kind,
                    previous_mrr_reais: "",
                    previous_catalog_plan_id: "",
                    subscription_id: "",
                    new_mrr_reais: "",
                  }));
                }}
              >
                <MenuItem value="new">{FINANCE_GLOSS.saleKindNewLabel}</MenuItem>
                <MenuItem value="addon">{FINANCE_GLOSS.saleKindAddonLabel}</MenuItem>
                <MenuItem value="upgrade">{FINANCE_GLOSS.saleKindUpgradeLabel}</MenuItem>
              </Select>
            </FormControl>
            <Autocomplete
              size="small"
              sx={{ minWidth: 280, flex: 1 }}
              options={wsOptions}
              loading={wsLoading}
              value={draft.dominio}
              onChange={(_, v) =>
                setDraft((d) => ({
                  ...d,
                  dominio: v,
                  subscription_id: "",
                  previous_catalog_plan_id: "",
                  previous_mrr_reais: "",
                }))
              }
              onInputChange={(_, value, reason) => {
                if (reason === "input") searchWorkspaces(value);
              }}
              onOpen={() => {
                if (!wsOptions.length) searchWorkspaces("");
              }}
              getOptionLabel={(o) => o?.label || ""}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Workspace"
                  placeholder="Buscar por nome…"
                  required
                  error={Boolean(fieldErrors.dominio) && !draft.dominio}
                  helperText={
                    !draft.dominio ? fieldErrors.dominio || "Obrigatório" : draft.dominio.schema || " "
                  }
                />
              )}
            />
            <TextField
              size="small"
              label="Data da venda"
              type="date"
              required
              InputLabelProps={{ shrink: true }}
              value={draft.sold_on}
              onChange={(e) => {
                const sold_on = e.target.value;
                setDraft((d) => ({ ...d, sold_on }));
              }}
              onBlur={() => {
                if (!isDateInYearMonth(draft.sold_on, yearMonth) && dateBounds.min) {
                  setDraft((d) => ({ ...d, sold_on: dateBounds.min }));
                }
              }}
              inputProps={{ min: dateBounds.min, max: dateBounds.max }}
              error={Boolean(fieldErrors.sold_on)}
              helperText={fieldErrors.sold_on || `Competência ${formatFinanceDateBr(yearMonth)}`}
              sx={{ width: 200 }}
            />
          </Stack>

          <Alert
            severity={draft.sale_kind === "new" ? "info" : "warning"}
            variant="outlined"
          >
            {saleKindHint(draft.sale_kind)}
          </Alert>

          {draft.sale_kind === "upgrade" ? (
            <Stack spacing={1.5}>
              <FormControl
                size="small"
                sx={{ minWidth: 320, maxWidth: 640 }}
                error={Boolean(fieldErrors.subscription)}
                required={activeSubOptions.length > 1}
              >
                <InputLabel>Assinatura do upgrade</InputLabel>
                <Select
                  label="Assinatura do upgrade"
                  value={draft.subscription_id}
                  disabled={!draft.dominio?.id || subsLoading}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      subscription_id: e.target.value,
                      new_mrr_reais: "",
                    }))
                  }
                >
                  <MenuItem value="">
                    <em>
                      {subsLoading
                        ? "Carregando…"
                        : activeSubOptions.length > 1
                          ? "Qual assinatura foi migrada?…"
                          : draft.dominio?.id
                            ? "Assinatura ativa…"
                            : "Selecione o workspace primeiro…"}
                    </em>
                  </MenuItem>
                  {activeSubOptions.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>
                      {s.name}
                      {s.sku ? ` · ${s.sku}` : ""}
                      {" · margem "}
                      {formatBrlFromCents(s.margin_cents)}
                    </MenuItem>
                  ))}
                </Select>
                <Typography
                  variant="caption"
                  color={fieldErrors.subscription ? "error" : "text.secondary"}
                  sx={{ mt: 0.5, ml: 1.75 }}
                >
                  {fieldErrors.subscription ||
                    (activeSubOptions.length > 1
                      ? "Obrigatório quando há mais de uma assinatura — a margem nova é só desta."
                      : "Margem nova = desta assinatura (não do portfólio inteiro).")}
                </Typography>
              </FormControl>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} useFlexGap flexWrap="wrap">
                <FormControl
                  size="small"
                  sx={{ minWidth: 320, flex: 1 }}
                  error={Boolean(fieldErrors.previous_plan)}
                  required
                >
                  <InputLabel>Plano anterior</InputLabel>
                  <Select
                    label="Plano anterior"
                    value={draft.previous_catalog_plan_id}
                    onChange={(e) => {
                      const previous_catalog_plan_id = e.target.value;
                      const opt = previousPlanOptions.find(
                        (p) => String(p.id) === String(previous_catalog_plan_id)
                      );
                      setDraft((d) => ({
                        ...d,
                        previous_catalog_plan_id,
                        previous_mrr_reais: opt
                          ? centsToReaisInput(opt.margin_cents)
                          : d.previous_mrr_reais,
                      }));
                    }}
                  >
                    <MenuItem value="">
                      <em>Selecione após migrar no workspace…</em>
                    </MenuItem>
                    {previousPlanOptions.map((p) => (
                      <MenuItem key={p.id} value={String(p.id)}>
                        {p.name}
                        {p.sku ? ` · ${p.sku}` : ""}
                        {" · margem "}
                        {formatBrlFromCents(p.margin_cents)}
                        {p.source === "cancelled_sub" ? " (encerrado neste WS)" : ""}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography
                    variant="caption"
                    color={fieldErrors.previous_plan ? "error" : "text.secondary"}
                    sx={{ mt: 0.5, ml: 1.75 }}
                  >
                    {fieldErrors.previous_plan ||
                      "Use um plano encerrado do workspace ou qualquer plano do catálogo."}
                  </Typography>
                </FormControl>
                <TextField
                  size="small"
                  label="Margem anterior (ajuste)"
                  value={draft.previous_mrr_reais}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      previous_mrr_reais: sanitizeDecimalTyping(e.target.value),
                    }))
                  }
                  error={Boolean(fieldErrors.previous_mrr)}
                  helperText={fieldErrors.previous_mrr || "Preenchida pelo plano; edite só se precisar"}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                  inputProps={{ inputMode: "decimal", autoComplete: "off" }}
                  sx={{ width: 200 }}
                />
              </Stack>
            </Stack>
          ) : null}

          {draft.sale_kind === "addon" ? (
            <FormControl
              size="small"
              sx={{ minWidth: 320, maxWidth: 560 }}
              error={Boolean(fieldErrors.subscription)}
              required
            >
              <InputLabel>Nova assinatura</InputLabel>
              <Select
                label="Nova assinatura"
                value={draft.subscription_id}
                disabled={!draft.dominio?.id || subsLoading}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    subscription_id: e.target.value,
                    new_mrr_reais: "",
                  }))
                }
              >
                <MenuItem value="">
                  <em>
                    {subsLoading
                      ? "Carregando…"
                      : draft.dominio?.id
                        ? "Selecione a assinatura adicionada…"
                        : "Selecione o workspace primeiro…"}
                  </em>
                </MenuItem>
                {activeSubOptions.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>
                      {s.name}
                      {s.sku ? ` · ${s.sku}` : ""}
                      {" · margem "}
                      {formatBrlFromCents(s.margin_cents)}
                    </MenuItem>
                  ))}
              </Select>
              <Typography
                variant="caption"
                color={fieldErrors.subscription ? "error" : "text.secondary"}
                sx={{ mt: 0.5, ml: 1.75 }}
              >
                {fieldErrors.subscription ||
                  "Só esta assinatura entra na base — o restante do cliente não é comissionado de novo."}
              </Typography>
            </FormControl>
          ) : null}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} useFlexGap flexWrap="wrap">
            <FormControl
              size="small"
              sx={{ minWidth: 220, flex: 1 }}
              required
              error={Boolean(fieldErrors.closer)}
            >
              <InputLabel>Closer (quem fechou)</InputLabel>
              <Select
                label="Closer (quem fechou)"
                value={draft.closer_person_id}
                onChange={(e) => {
                  const closer_person_id = e.target.value;
                  setDraft((d) => ({
                    ...d,
                    closer_person_id,
                    indicator_person_id:
                      String(d.indicator_person_id) === String(closer_person_id)
                        ? ""
                        : d.indicator_person_id,
                  }));
                }}
              >
                <MenuItem value="">
                  <em>Selecione…</em>
                </MenuItem>
                {activePeople.map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.closer ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {fieldErrors.closer}
                </Typography>
              ) : null}
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 220, flex: 1 }}
              error={Boolean(fieldErrors.indicator)}
            >
              <InputLabel>Indicador (opcional)</InputLabel>
              <Select
                label="Indicador (opcional)"
                value={draft.indicator_person_id}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, indicator_person_id: e.target.value }))
                }
              >
                <MenuItem value="">
                  <em>Sem indicação</em>
                </MenuItem>
                {activePeople
                  .filter((p) => String(p.id) !== String(draft.closer_person_id))
                  .map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </MenuItem>
                  ))}
              </Select>
              {fieldErrors.indicator ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {fieldErrors.indicator}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                  Não pode ser a mesma pessoa do closer
                </Typography>
              )}
            </FormControl>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} useFlexGap flexWrap="wrap">
            <TextField
              size="small"
              label={
                draft.sale_kind === "upgrade"
                  ? "Margem nova (após migração)"
                  : draft.sale_kind === "addon"
                    ? "Margem da assinatura"
                    : "Margem (R$)"
              }
              value={draft.new_mrr_reais}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  new_mrr_reais: sanitizeDecimalTyping(e.target.value),
                }))
              }
              error={Boolean(fieldErrors.new_mrr)}
              helperText={
                fieldErrors.new_mrr ||
                (draft.sale_kind === "upgrade"
                  ? "Vazio = margem atual do workspace (MRR − CMV − impostos)"
                  : draft.sale_kind === "addon"
                    ? "Vazio = margem só da assinatura selecionada"
                    : "Vazio = margem do catálogo (MRR − CMV − impostos)")
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
              inputProps={{ inputMode: "decimal", autoComplete: "off" }}
              sx={{ width: 220 }}
            />
            <TextField
              size="small"
              label="Notas"
              value={draft.notes}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  notes: e.target.value.slice(0, FIELD_LIMITS.notesMax),
                }))
              }
              error={Boolean(fieldErrors.notes)}
              helperText={
                fieldErrors.notes ||
                `${String(draft.notes || "").length}/${FIELD_LIMITS.notesMax}`
              }
              sx={{ minWidth: 200, flex: 1 }}
            />
            <BusyButton
              variant="contained"
              loading={saving}
              disabled={!canSave}
              onClick={save}
              sx={{ alignSelf: "flex-start" }}
            >
              Confirmar venda
            </BusyButton>
          </Stack>

          {preview?.error ? (
            <Alert severity="warning">{preview.error}</Alert>
          ) : preview?.preview?.margin_missing ? (
            <Alert severity="warning">
              {preview.preview.warning ||
                "Cadastre CMV/margem no Catálogo antes de lançar, ou informe a margem manualmente."}
            </Alert>
          ) : preview?.preview ? (
            <Alert severity="success" variant="outlined">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Base (margem empresa): {formatBrlFromCents(baseCents)}
                {preview.preview.plan_name ? ` · ${preview.preview.plan_name}` : ""}
              </Typography>
              {preview.preview.catalog_mrr_cents != null ? (
                <Typography variant="caption" display="block" color="text.secondary">
                  MRR {formatBrlFromCents(preview.preview.catalog_mrr_cents)}
                  {" − "}
                  CMV {formatBrlFromCents(preview.preview.catalog_cmv_cents || 0)}
                  {" − "}
                  impostos {formatBrlFromCents(preview.preview.catalog_tax_cents || 0)}
                  {" = "}
                  margem {formatBrlFromCents(preview.preview.plan_margin_cents ?? baseCents)}
                  {preview.preview.addons_margin_cents
                    ? ` · pacotes ${formatBrlFromCents(preview.preview.addons_margin_cents)}`
                    : ""}
                </Typography>
              ) : null}
              {draft.sale_kind === "upgrade" ? (
                <Typography variant="caption" display="block" color="text.secondary">
                  Delta: margem da assinatura {formatBrlFromCents(preview.preview.new_margin_cents)} −
                  anterior {formatBrlFromCents(preview.preview.previous_margin_cents)}
                  {preview.preview.previous_plan_name
                    ? ` (${preview.preview.previous_plan_name})`
                    : ""}
                </Typography>
              ) : null}
              {draft.sale_kind === "addon" ? (
                <Typography variant="caption" display="block" color="text.secondary">
                  Base só da assinatura selecionada (aditivo) — não é contrato de novo cliente.
                </Typography>
              ) : null}
              {payoutLines.length ? (
                <Stack spacing={0.25} sx={{ mt: 1 }}>
                  {payoutLines.map((l, idx) => (
                    <Typography key={idx} variant="caption" display="block">
                      {LINE_ROLE_LABEL[l.line_role] || l.line_role}: {l.person_name || `#${l.person_id}`} ·{" "}
                      {formatPct(l.rate_pct)} / mês → {formatBrlFromCents(l.monthly_payout_cents ?? l.payout_cents)}
                      {l.total_if_full_cents != null
                        ? ` · até ${formatBrlFromCents(l.total_if_full_cents)} em ${preview.payout?.payout_months || 12} meses`
                        : ""}
                    </Typography>
                  ))}
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Parcela do mês: {formatBrlFromCents(preview.payout?.monthly_payout_cents)} · Se vestir{" "}
                    {preview.payout?.payout_months || 12} meses:{" "}
                    {formatBrlFromCents(preview.payout?.total_if_full_cents)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {FINANCE_GLOSS.teamVestingRules}
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Selecione o closer para ver o preview das comissões.
                </Typography>
              )}
            </Alert>
          ) : null}
        </Stack>
      </FinanceSection>

      <FinanceSection
        title={`Vendas em ${formatFinanceDateBr(yearMonth)}`}
        hint="Cancelar remove as linhas de comissão desta venda."
        action={
          items.length ? <Chip size="small" variant="outlined" label={`${items.length} venda(s)`} /> : null
        }
      >
        {!items.length ? (
          <FinanceEmptyState
            title="Nenhuma venda nesta competência"
            description="Lance a primeira venda acima. Sem indicação, deixe o indicador em branco."
          />
        ) : (
          <FinanceTableShell>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Workspace</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Closer</TableCell>
                  <TableCell>Indicador</TableCell>
                  <TableCell align="right">Base</TableCell>
                  <TableCell align="right">Parcela/mês</TableCell>
                  <TableCell align="right">Até 12 meses</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>
                      {s.dominio_name || `Workspace #${s.dominio_id}`}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {formatFinanceDateBr(s.sold_on)}
                        {s.installments
                          ? ` · ${s.installments.earned || 0} pagas / ${s.installments.scheduled || 0} a vencer`
                          : ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={SALE_KIND_CHIP[s.sale_kind] || s.sale_kind || "Novo"}
                      />
                    </TableCell>
                    <TableCell>{s.closer_name || `Pessoa #${s.closer_person_id}`}</TableCell>
                    <TableCell>{s.indicator_name || "—"}</TableCell>
                    <TableCell align="right">{formatBrlFromCents(s.base_cents)}</TableCell>
                    <TableCell align="right">
                      {formatBrlFromCents(s.monthly_payout_cents ?? s.total_payout_cents)}
                    </TableCell>
                    <TableCell align="right">{formatBrlFromCents(s.total_if_full_cents)}</TableCell>
                    <TableCell align="right">
                      {s.status === "open" ? (
                        <BusyButton size="small" loading={saving} onClick={() => cancelSale(s.id)}>
                          Cancelar
                        </BusyButton>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </FinanceTableShell>
        )}
      </FinanceSection>
    </Stack>
  );
}

export function TeamRulesTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState({
    closer_commission_pct: 0.1,
    indicator_commission_pct: 0.05,
    leader_override_pct: 0.2,
    payout_months: 12,
  });
  const [defaultsUi, setDefaultsUi] = useState({
    closer: "10",
    indicator: "5",
    leader: "20",
    months: "12",
  });
  const [people, setPeople] = useState([]);
  const [peopleUi, setPeopleUi] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPlatformFinanceCommissionsTeamDefaults();
      if (res?.defaults) {
        setDefaults(res.defaults);
        setDefaultsUi({
          closer: pctToInput(res.defaults.closer_commission_pct),
          indicator: pctToInput(res.defaults.indicator_commission_pct),
          leader: pctToInput(res.defaults.leader_override_pct),
          months: String(res.defaults.payout_months ?? 12),
        });
      }
      const list = res?.people || [];
      setPeople(list);
      const ui = {};
      for (const p of list) {
        ui[p.id] = {
          closer: pctToInput(p.closer_commission_pct),
          indicator: pctToInput(p.indicator_commission_pct),
          leader: pctToInput(p.leader_override_pct),
        };
      }
      setPeopleUi(ui);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Não foi possível carregar réguas do time.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const defaultsErrors = useMemo(() => {
    const e = {};
    const closer = inputToPct(defaultsUi.closer, {
      maxPct: FIELD_LIMITS.teamCommissionPctMax,
      allowEmpty: false,
    });
    const indicator = inputToPct(defaultsUi.indicator, {
      maxPct: FIELD_LIMITS.teamCommissionPctMax,
      allowEmpty: false,
    });
    const leader = inputToPct(defaultsUi.leader, {
      maxPct: FIELD_LIMITS.leaderOverridePctMax,
      allowEmpty: false,
    });
    if (closer == null) e.closer = `Informe 0–${FIELD_LIMITS.teamCommissionPctMax}.`;
    if (indicator == null) e.indicator = `Informe 0–${FIELD_LIMITS.teamCommissionPctMax}.`;
    if (leader == null) e.leader = `Informe 0–${FIELD_LIMITS.leaderOverridePctMax}.`;
    const months = clampInt(defaultsUi.months, {
      min: FIELD_LIMITS.payoutMonthsMin,
      max: FIELD_LIMITS.payoutMonthsMax,
      fallback: NaN,
    });
    if (!Number.isFinite(months)) {
      e.months = `${FIELD_LIMITS.payoutMonthsMin}–${FIELD_LIMITS.payoutMonthsMax} meses.`;
    }
    return e;
  }, [defaultsUi]);

  const saveDefaults = async () => {
    if (Object.keys(defaultsErrors).length) {
      setMsg({ ok: false, text: Object.values(defaultsErrors)[0] });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      const payload = {
        closer_commission_pct: inputToPct(defaultsUi.closer, {
          maxPct: FIELD_LIMITS.teamCommissionPctMax,
          allowEmpty: false,
        }),
        indicator_commission_pct: inputToPct(defaultsUi.indicator, {
          maxPct: FIELD_LIMITS.teamCommissionPctMax,
          allowEmpty: false,
        }),
        leader_override_pct: inputToPct(defaultsUi.leader, {
          maxPct: FIELD_LIMITS.leaderOverridePctMax,
          allowEmpty: false,
        }),
        payout_months: clampInt(defaultsUi.months, {
          min: FIELD_LIMITS.payoutMonthsMin,
          max: FIELD_LIMITS.payoutMonthsMax,
          fallback: 12,
        }),
      };
      await putPlatformFinanceCommissionsTeamDefaults(payload);
      setDefaults(payload);
      setMsg({ ok: true, text: "Padrões do time salvos." });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.message || e?.message || "Falha ao salvar." });
    } finally {
      setSaving(false);
    }
  };

  const savePeople = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await putPlatformFinancePeople({
        items: people.map((p) => {
          const ui = peopleUi[p.id] || {};
          return {
            ...p,
            closer_commission_pct: inputToPct(ui.closer, {
              maxPct: FIELD_LIMITS.teamCommissionPctMax,
            }),
            indicator_commission_pct: inputToPct(ui.indicator, {
              maxPct: FIELD_LIMITS.teamCommissionPctMax,
            }),
            leader_override_pct: inputToPct(ui.leader, {
              maxPct: FIELD_LIMITS.leaderOverridePctMax,
            }),
          };
        }),
      });
      setMsg({ ok: true, text: "Percentuais por pessoa salvos." });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.message || e?.message || "Falha ao salvar pessoas." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <FinanceLoading />;
  if (error) return <FinanceAlertStack error={error} />;

  const pctAdornment = <InputAdornment position="end">%</InputAdornment>;

  return (
    <Stack spacing={2}>
      {msg ? <Alert severity={msg.ok ? "success" : "error"}>{msg.text}</Alert> : null}
      <Alert severity="info" variant="outlined">
        {FINANCE_GLOSS.teamRulesHint}
      </Alert>

      <FinanceSection title="Padrões globais" hint="Usados quando a pessoa não tem % próprio.">
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} useFlexGap flexWrap="wrap">
          <TextField
            size="small"
            label="Closer"
            value={defaultsUi.closer}
            onChange={(e) =>
              setDefaultsUi((d) => ({
                ...d,
                closer: sanitizePctTyping(e.target.value, {
                  maxPct: FIELD_LIMITS.teamCommissionPctMax,
                }),
              }))
            }
            onBlur={() =>
              setDefaultsUi((d) => ({
                ...d,
                closer: pctToInput(
                  inputToPct(d.closer, {
                    maxPct: FIELD_LIMITS.teamCommissionPctMax,
                    allowEmpty: false,
                  }) ?? 0
                ),
              }))
            }
            error={Boolean(defaultsErrors.closer)}
            helperText={defaultsErrors.closer || `0–${FIELD_LIMITS.teamCommissionPctMax} por mês`}
            InputProps={{ endAdornment: pctAdornment }}
            inputProps={{ inputMode: "decimal", autoComplete: "off" }}
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            label="Indicador"
            value={defaultsUi.indicator}
            onChange={(e) =>
              setDefaultsUi((d) => ({
                ...d,
                indicator: sanitizePctTyping(e.target.value, {
                  maxPct: FIELD_LIMITS.teamCommissionPctMax,
                }),
              }))
            }
            onBlur={() =>
              setDefaultsUi((d) => ({
                ...d,
                indicator: pctToInput(
                  inputToPct(d.indicator, {
                    maxPct: FIELD_LIMITS.teamCommissionPctMax,
                    allowEmpty: false,
                  }) ?? 0
                ),
              }))
            }
            error={Boolean(defaultsErrors.indicator)}
            helperText={defaultsErrors.indicator || `0–${FIELD_LIMITS.teamCommissionPctMax} por mês`}
            InputProps={{ endAdornment: pctAdornment }}
            inputProps={{ inputMode: "decimal", autoComplete: "off" }}
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            label="Gestor sobre comissão"
            value={defaultsUi.leader}
            onChange={(e) =>
              setDefaultsUi((d) => ({
                ...d,
                leader: sanitizePctTyping(e.target.value, {
                  maxPct: FIELD_LIMITS.leaderOverridePctMax,
                }),
              }))
            }
            onBlur={() =>
              setDefaultsUi((d) => ({
                ...d,
                leader: pctToInput(
                  inputToPct(d.leader, {
                    maxPct: FIELD_LIMITS.leaderOverridePctMax,
                    allowEmpty: false,
                  }) ?? 0
                ),
              }))
            }
            error={Boolean(defaultsErrors.leader)}
            helperText={defaultsErrors.leader || "% da parcela mensal do liderado"}
            InputProps={{ endAdornment: pctAdornment }}
            inputProps={{ inputMode: "decimal", autoComplete: "off" }}
            sx={{ width: 200 }}
          />
          <TextField
            size="small"
            label="Meses"
            value={defaultsUi.months}
            onChange={(e) =>
              setDefaultsUi((d) => ({ ...d, months: sanitizeIntTyping(e.target.value) }))
            }
            onBlur={() =>
              setDefaultsUi((d) => ({
                ...d,
                months: String(
                  clampInt(d.months, {
                    min: FIELD_LIMITS.payoutMonthsMin,
                    max: FIELD_LIMITS.payoutMonthsMax,
                    fallback: 12,
                  })
                ),
              }))
            }
            error={Boolean(defaultsErrors.months)}
            helperText={
              defaultsErrors.months ||
              `${FIELD_LIMITS.payoutMonthsMin}–${FIELD_LIMITS.payoutMonthsMax} parcelas`
            }
            inputProps={{ inputMode: "numeric", autoComplete: "off" }}
            sx={{ width: 110 }}
          />
          <BusyButton
            variant="contained"
            loading={saving}
            disabled={Object.keys(defaultsErrors).length > 0}
            onClick={saveDefaults}
          >
            Salvar padrões
          </BusyButton>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {FINANCE_GLOSS.teamVestingRules}
        </Typography>
      </FinanceSection>

      <FinanceSection
        title="Por pessoa"
        hint="Deixe vazio para herdar o padrão. O % do gestor vale quando um liderado fecha ou indica."
      >
        {!people.length ? (
          <FinanceEmptyState
            title="Nenhuma pessoa no ciclo"
            description="Cadastre o time em Incentivos → Pessoas."
          />
        ) : (
          <>
            <FinanceTableShell>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Pessoa</TableCell>
                    <TableCell>Gestor</TableCell>
                    <TableCell align="right">Closer %</TableCell>
                    <TableCell align="right">Indicador %</TableCell>
                    <TableCell align="right">Gestor %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {people.map((p) => {
                    const ui = peopleUi[p.id] || { closer: "", indicator: "", leader: "" };
                    return (
                      <TableRow key={p.id}>
                        <TableCell>{p.name}</TableCell>
                        <TableCell>{p.manager_name || "—"}</TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            value={ui.closer}
                            placeholder={pctToInput(defaults.closer_commission_pct)}
                            onChange={(e) =>
                              setPeopleUi((prev) => ({
                                ...prev,
                                [p.id]: {
                                  ...ui,
                                  closer: sanitizePctTyping(e.target.value, {
                                    maxPct: FIELD_LIMITS.teamCommissionPctMax,
                                  }),
                                },
                              }))
                            }
                            onBlur={() =>
                              setPeopleUi((prev) => ({
                                ...prev,
                                [p.id]: {
                                  ...ui,
                                  closer:
                                    ui.closer === ""
                                      ? ""
                                      : pctToInput(
                                          inputToPct(ui.closer, {
                                            maxPct: FIELD_LIMITS.teamCommissionPctMax,
                                          })
                                        ),
                                },
                              }))
                            }
                            InputProps={{ endAdornment: pctAdornment }}
                            inputProps={{ inputMode: "decimal", autoComplete: "off" }}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            value={ui.indicator}
                            placeholder={pctToInput(defaults.indicator_commission_pct)}
                            onChange={(e) =>
                              setPeopleUi((prev) => ({
                                ...prev,
                                [p.id]: {
                                  ...ui,
                                  indicator: sanitizePctTyping(e.target.value, {
                                    maxPct: FIELD_LIMITS.teamCommissionPctMax,
                                  }),
                                },
                              }))
                            }
                            onBlur={() =>
                              setPeopleUi((prev) => ({
                                ...prev,
                                [p.id]: {
                                  ...ui,
                                  indicator:
                                    ui.indicator === ""
                                      ? ""
                                      : pctToInput(
                                          inputToPct(ui.indicator, {
                                            maxPct: FIELD_LIMITS.teamCommissionPctMax,
                                          })
                                        ),
                                },
                              }))
                            }
                            InputProps={{ endAdornment: pctAdornment }}
                            inputProps={{ inputMode: "decimal", autoComplete: "off" }}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            value={ui.leader}
                            placeholder={pctToInput(defaults.leader_override_pct)}
                            onChange={(e) =>
                              setPeopleUi((prev) => ({
                                ...prev,
                                [p.id]: {
                                  ...ui,
                                  leader: sanitizePctTyping(e.target.value, {
                                    maxPct: FIELD_LIMITS.leaderOverridePctMax,
                                  }),
                                },
                              }))
                            }
                            onBlur={() =>
                              setPeopleUi((prev) => ({
                                ...prev,
                                [p.id]: {
                                  ...ui,
                                  leader:
                                    ui.leader === ""
                                      ? ""
                                      : pctToInput(
                                          inputToPct(ui.leader, {
                                            maxPct: FIELD_LIMITS.leaderOverridePctMax,
                                          })
                                        ),
                                },
                              }))
                            }
                            InputProps={{ endAdornment: pctAdornment }}
                            inputProps={{ inputMode: "decimal", autoComplete: "off" }}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </FinanceTableShell>
            <BusyButton variant="outlined" loading={saving} onClick={savePeople} sx={{ mt: 1.5 }}>
              Salvar percentuais
            </BusyButton>
          </>
        )}
      </FinanceSection>
    </Stack>
  );
}
